/**
 * Drawing a whole filtered set out of a paged endpoint.
 *
 * Asking for a large page is a request, not an instruction. DRF ignores
 * `page_size` unless the view opts in, and caps it when it does, so the size
 * the server actually served is read back rather than assumed. Pages are then
 * drawn until the count is met.
 *
 * Two things this must never do. It must not loop forever against a server
 * that ignores `page` as well, and it must not count a row twice -- a total
 * that overstates the money is worse than one that understates it, because
 * nothing on the screen looks wrong. Rows already held are dropped, and a page
 * that adds none ends the walk.
 *
 * Pure but for the fetch it is handed. The caller decides what a page is.
 */

/** How many requests one panel may spend before it settles for what it has. */
export const MAX_PAGES = 10;

/** Rows are told apart by id; one without an id cannot be a duplicate. */
const keyOf = (row) => {
  const id = row && row.id;
  return id === null || id === undefined ? null : String(id);
};

const countOf = (data) => {
  const sent = Number(data && data.count);
  return Number.isFinite(sent) ? sent : null;
};

export async function fetchAllRows(fetchPage, options = {}) {
  const { pageSize, maxPages = MAX_PAGES } = options;

  let rows = [];
  let claimed = null;

  for (let page = 1; page <= maxPages; page += 1) {
    // Sequential by necessity: how many pages there are is only known from
    // the page before it.
    // eslint-disable-next-line no-await-in-loop
    const data = await fetchPage(page, pageSize);
    const batch = (data && data.results) || [];

    if (claimed === null) claimed = countOf(data);

    const held = new Set(rows.map(keyOf).filter((key) => key !== null));
    const fresh = batch.filter((row) => {
      const key = keyOf(row);
      return key === null || !held.has(key);
    });

    if (fresh.length === 0) break;
    rows = rows.concat(fresh);
    if (claimed !== null && rows.length >= claimed) break;
  }

  return { rows, count: claimed === null ? rows.length : claimed };
}
